import type { FlowDto } from '@/types/dtos/flows/response/flow.dto.ts';
import { useQuery } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { getObjectId } from '@/helpers/object.id.helper';
import { liveContextVmFingerprint } from '@/helpers/live-context-vm.helper';
import { mergeFlowPreservingLiveContext } from '@/helpers/merge-flow-preserving-live-context.helper';
import { ServerError } from '@/types/errors/server.error';
import { useEffect, useMemo } from 'react';

const isDebugFlowEnabled = () =>
  import.meta.env.DEV && Boolean((window as { __DEBUG_FLOW__?: boolean }).__DEBUG_FLOW__);
const debugFlow = (...args: unknown[]) => {
  if (isDebugFlowEnabled()) {
    console.log(...args);
  }
};

export const GET_FLOW_QUERY_KEY = 'get-flow';
let lastLoggedFlowId: string | undefined;

// Query flow by originId - backend decides what mode documents to return unless mode is set
type FlowQueryOptions = {
  refetchOnWindowFocus?: boolean;
  refetchOnReconnect?: boolean;
  syncToStore?: boolean;
  staleTime?: number;
  refetchOnMount?: boolean;
  /** When set, requests a single mode document (`read_${mode}`). Contributors need ORGANIZE. */
  mode?: FlowDto['mode'];
};

export const useGetFlowQuery = (id?: string, eventSlotId?: string | null, options: FlowQueryOptions = {}) => {
  const { getToken } = useAuth();
  const currentFlow = useFlowStore((s) => s.flow);
  const originId = id ?? (currentFlow?.origin ? getObjectId(currentFlow.origin) : undefined);
  const normalizedEventSlotId = eventSlotId ?? undefined;
  const {
    refetchOnWindowFocus = true,
    refetchOnReconnect = true,
    syncToStore = true,
    staleTime,
    refetchOnMount,
    mode,
  } = options;

  const query = useQuery({
    queryKey: mode
      ? [GET_FLOW_QUERY_KEY, originId, normalizedEventSlotId, mode]
      : [GET_FLOW_QUERY_KEY, originId, normalizedEventSlotId],
    queryFn: async () => {
      const token = await getToken();
      return getFlow({ id: originId!, token: token ?? '', eventSlotId: normalizedEventSlotId, mode });
    },
    enabled: !!originId,
    refetchOnWindowFocus,
    refetchOnReconnect,
    staleTime,
    refetchOnMount,
  });

  const normalized = useMemo(() => {
    if (!query.data) {
      return { flows: [] as FlowDto[], selectedFlow: undefined as FlowDto | undefined };
    }

    const flows = Array.isArray(query.data) ? query.data : [query.data];

    // Pick by id / origin matching the requested id
    let selectedFlow =
      flows.find((f) => f.id === originId) ??
      flows.find((f) => {
        const fOriginId = typeof f.origin === 'string' ? f.origin : f.origin?.id;
        return fOriginId === originId;
      }) ??
      flows[0]; // Fallback to first flow

    // If selected flow doesn't have slots, try to find one that does
    if (!selectedFlow?.slots || selectedFlow.slots.length === 0) {
      const flowWithSlots = flows.find((f) => f.slots && f.slots.length > 0);
      if (flowWithSlots) {
        debugFlow('[FlowDetail] Selected flow has no slots, using flow with slots instead', {
          selectedFlowId: selectedFlow?.id,
          flowWithSlotsId: flowWithSlots.id,
        });
        selectedFlow = flowWithSlots;
      }
    }

    return { flows, selectedFlow };
  }, [query.data, originId]);

  const slotsHaveChanged = (currentSlots?: FlowDto['slots'], nextSlots?: FlowDto['slots']) => {
    if (!currentSlots && !nextSlots) return false;
    if (!currentSlots || !nextSlots) return true;
    if (currentSlots.length !== nextSlots.length) return true;
    // Order matters for cue sheet / Control list — same ids in a new sequence must sync.
    if (currentSlots.some((slot, index) => slot.id !== nextSlots[index]?.id)) return true;
    const currentById = new Map(currentSlots.map((slot) => [slot.id, slot]));
    return nextSlots.some((nextSlot) => {
      const currentSlot = currentById.get(nextSlot.id);
      if (!currentSlot) return true;
      if (typeof currentSlot.content !== typeof nextSlot.content) return true;
      if (typeof nextSlot.content === 'string' || typeof currentSlot.content === 'string') {
        return currentSlot.content !== nextSlot.content;
      }
      if (!nextSlot.content || !currentSlot.content) return nextSlot.content !== currentSlot.content;
      // Detect when content transitions between unpopulated fallback (no id) and a fully populated session
      const currentHasId = 'id' in currentSlot.content;
      const nextHasId = 'id' in nextSlot.content;
      if (currentHasId !== nextHasId) return true;
      if (currentHasId && nextHasId && currentSlot.content.id !== nextSlot.content.id) {
        return true;
      }
      if (
        'version' in nextSlot.content &&
        'version' in currentSlot.content &&
        nextSlot.content.version !== currentSlot.content.version
      ) {
        return true;
      }
      if (
        'title' in nextSlot.content &&
        'title' in currentSlot.content &&
        nextSlot.content.title !== currentSlot.content.title
      ) {
        return true;
      }
      return false;
    });
  };

  const mediaHasChanged = (currentMedia?: FlowDto['media'], nextMedia?: FlowDto['media']) => {
    if (!currentMedia && !nextMedia) return false;
    if (!currentMedia || !nextMedia) return true;
    if (currentMedia.length !== nextMedia.length) return true;
    const currentById = new Map(currentMedia.map((media) => [media.id, media]));
    return nextMedia.some((nextMediaItem) => {
      const currentMediaItem = currentById.get(nextMediaItem.id);
      if (!currentMediaItem) return true;
      // Check if media item properties changed
      return (
        currentMediaItem.id !== nextMediaItem.id ||
        currentMediaItem.title !== nextMediaItem.title ||
        currentMediaItem.isRequested !== nextMediaItem.isRequested ||
        JSON.stringify(currentMediaItem.assets) !== JSON.stringify(nextMediaItem.assets)
      );
    });
  };

  const mediaSettingsHasChanged = (current?: FlowDto['mediaSettings'], next?: FlowDto['mediaSettings']) =>
    JSON.stringify(current ?? null) !== JSON.stringify(next ?? null);

  const flowMediaPolicyHasChanged = (current?: FlowDto, next?: FlowDto) => {
    if (!current || !next) return false;
    return (
      mediaSettingsHasChanged(current.mediaSettings, next.mediaSettings) ||
      JSON.stringify(current.effectiveMediaSettings ?? null) !== JSON.stringify(next.effectiveMediaSettings ?? null) ||
      JSON.stringify(current.mediaSettingsCapabilities ?? null) !==
        JSON.stringify(next.mediaSettingsCapabilities ?? null)
    );
  };

  const liveContextHasChanged = (
    currentLiveContext?: FlowDto['liveContext'],
    nextLiveContext?: FlowDto['liveContext'],
  ) => {
    if (!currentLiveContext && !nextLiveContext) return false;
    if (!currentLiveContext || !nextLiveContext) return true;
    if (currentLiveContext.isRunning !== nextLiveContext.isRunning) return true;
    if (currentLiveContext.showMode !== nextLiveContext.showMode) return true;
    const currentVmKey = currentLiveContext.vms?.map(liveContextVmFingerprint).join('|') ?? '';
    const nextVmKey = nextLiveContext.vms?.map(liveContextVmFingerprint).join('|') ?? '';
    return currentVmKey !== nextVmKey;
  };

  const mediaPlaylistsHaveChanged = (current?: FlowDto['mediaPlaylists'], next?: FlowDto['mediaPlaylists']) =>
    JSON.stringify(current ?? null) !== JSON.stringify(next ?? null);

  useEffect(() => {
    if (!syncToStore) return;
    if (normalized.flows.length === 0) return;

    const currentStore = useFlowStore.getState();
    const selectedFlow = normalized.selectedFlow;
    const planFlow = normalized.flows.find((f) => f.mode === 'PLAN');
    const organizeFlow = normalized.flows.find((f) => f.mode === 'ORGANIZE');
    if (
      planFlow &&
      (currentStore.flowPlan?.version !== planFlow.version ||
        JSON.stringify(currentStore.flowPlan?.publishedModes) !== JSON.stringify(planFlow.publishedModes) ||
        slotsHaveChanged(currentStore.flowPlan?.slots, planFlow.slots) ||
        mediaHasChanged(currentStore.flowPlan?.media, planFlow.media) ||
        mediaPlaylistsHaveChanged(currentStore.flowPlan?.mediaPlaylists, planFlow.mediaPlaylists) ||
        liveContextHasChanged(currentStore.flowPlan?.liveContext, planFlow.liveContext) ||
        flowMediaPolicyHasChanged(currentStore.flowPlan, planFlow))
    ) {
      useFlowStore.setState({
        flowPlan: mergeFlowPreservingLiveContext(planFlow, currentStore.flowPlan ?? currentStore.flow),
      });
    }
    if (
      organizeFlow &&
      (currentStore.flowOrganize?.version !== organizeFlow.version ||
        JSON.stringify(currentStore.flowOrganize?.publishedModes) !== JSON.stringify(organizeFlow.publishedModes) ||
        slotsHaveChanged(currentStore.flowOrganize?.slots, organizeFlow.slots) ||
        mediaHasChanged(currentStore.flowOrganize?.media, organizeFlow.media) ||
        mediaPlaylistsHaveChanged(currentStore.flowOrganize?.mediaPlaylists, organizeFlow.mediaPlaylists) ||
        liveContextHasChanged(currentStore.flowOrganize?.liveContext, organizeFlow.liveContext) ||
        flowMediaPolicyHasChanged(currentStore.flowOrganize, organizeFlow))
    ) {
      useFlowStore.setState({
        flowOrganize: mergeFlowPreservingLiveContext(organizeFlow, currentStore.flowOrganize ?? currentStore.flow),
      });
    }

    const currentFlow = currentStore.flow;
    if (
      selectedFlow &&
      currentFlow &&
      (currentFlow.id === selectedFlow.id || getObjectId(currentFlow.origin) === getObjectId(selectedFlow.origin))
    ) {
      const liveContextChanged = liveContextHasChanged(currentFlow.liveContext, selectedFlow.liveContext);
      const structureChanged =
        slotsHaveChanged(currentFlow.slots, selectedFlow.slots) ||
        mediaHasChanged(currentFlow.media, selectedFlow.media) ||
        mediaPlaylistsHaveChanged(currentFlow.mediaPlaylists, selectedFlow.mediaPlaylists);
      if (liveContextChanged || structureChanged) {
        useFlowStore.setState({
          flow: mergeFlowPreservingLiveContext(selectedFlow, currentFlow),
        });
      } else if (flowMediaPolicyHasChanged(currentFlow, selectedFlow)) {
        useFlowStore.setState({ flow: selectedFlow });
      }
    }

    const shouldLog = !lastLoggedFlowId || lastLoggedFlowId !== selectedFlow?.id;
    if (shouldLog) {
      lastLoggedFlowId = selectedFlow?.id;
      debugFlow('[FlowDetail] Backend flow response:', {
        flowId: selectedFlow?.id,
        flowOrigin: selectedFlow?.origin,
        eventSlotIdParam: eventSlotId,
        eventSlotFromBackend: selectedFlow?.slot,
        eventSlotId: selectedFlow?.slot?.id,
        eventSlotStartOffset: selectedFlow?.slot?.startOffsetMinutes,
        eventSlotDayId: selectedFlow?.slot?.day?.id,
        eventSlotVersion: selectedFlow?.slot?.version,
        startDate: selectedFlow?.startDate,
        hasSlots: !!selectedFlow?.slots,
        slotsLength: selectedFlow?.slots?.length,
      });
    }
  }, [normalized.flows, normalized.selectedFlow, eventSlotId, syncToStore]);

  return {
    ...query,
    flows: normalized.flows,
    data: normalized.selectedFlow,
  };
};

const getFlow = async ({
  id,
  token,
  eventSlotId,
  mode,
}: {
  id: string;
  token: string;
  eventSlotId?: string;
  mode?: FlowDto['mode'];
}): Promise<FlowDto | FlowDto[]> => {
  // Query with originId - backend provides appropriate document(s) based on access
  // If eventSlotId is provided, backend will return the specific eventSlot instead of first matching one
  const url = new URL(`${import.meta.env.VITE_API_URL}/flows/${id}`);
  if (eventSlotId) {
    url.searchParams.set('eventSlotId', eventSlotId);
  }
  if (mode) {
    url.searchParams.set('mode', mode);
  }
  const flowData = await sendJSONRequest({ url: url.toString(), method: METHOD.GET, token });

  debugFlow('[FlowDetail] Raw backend response:', flowData);
  debugFlow('[FlowDetail] Is array?', Array.isArray(flowData));

  // Return as-is - normalization happens in select function
  return flowData;
};

/** PLAY documents are merged into PLAN on origin reads; probe existence via mode=PLAY. */
export const useHasPlayFlowLayerQuery = (originId?: string) => {
  const { getToken } = useAuth();

  return useQuery({
    queryKey: [GET_FLOW_QUERY_KEY, originId, 'PLAY-layer'],
    queryFn: async () => {
      const token = await getToken();
      const flow = await getFlow({ id: originId!, token: token ?? '', mode: 'PLAY' });
      return Array.isArray(flow) ? flow[0] : flow;
    },
    enabled: !!originId,
    retry: (failureCount, error) => {
      if (error instanceof ServerError && error.statusCode === 404) return false;
      return failureCount < 2;
    },
    staleTime: 30_000,
  });
};
